import { Injectable } from '@angular/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { IdentityService } from 'main/services/identity.service'
import { NaturalOrderByPipe } from 'common/util/pipes/naturalOrderBy.pipe'
import { ElementsGroup, ElementsUser } from 'elements-sdk'
import { ObjectPickerModal } from '../components/objectPickerModal.component'
import { PickerOptions } from '../types'

@Injectable({ providedIn: 'root' })
export class PickerService {
  constructor (
    private ngbModal: NgbModal,
    private translate: TranslateService,
    private api: APIService,
    private identity: IdentityService,
  ) { }

  async pickUser (
    users: ElementsUser[] | Promise<ElementsUser[]> | null = null,
  ): Promise<ElementsUser> {
    return (await this.pickUsersAndGroups(users, [], { multiple: false }))[0] as unknown as ElementsUser
  }

  pickUsersAndGroups (
    users: ElementsUser[] | Promise<ElementsUser[]> | null = null,
    groups: ElementsGroup[] | Promise<ElementsGroup[]> | null = null,
    options: Partial<PickerOptions<ElementsUser|ElementsGroup>> = {},
  ): Promise<(ElementsUser | ElementsGroup)[]> {
    users ??= this.api.main.getAllUsers({})
    if (!groups) {
      if (this.identity.hasPermission('users:view')) {
        groups = this.api.main.getAllGroups({})
      } else {
        groups = []
      }
    }

    users = Promise.resolve(users).then(x => new NaturalOrderByPipe().transform(x, ['display_name']))
    groups = Promise.resolve(groups).then(x => new NaturalOrderByPipe().transform(x, ['name']))

    const items = Promise.all([users, groups]).then(([resolvedUsers, resolvedGroups]) => [
      ...resolvedUsers.map(user => ({
        name: user.displayName,
        group: this.translate.instant('Users'),
        iconClass: 'far fa-user',
        object: user,
      })),
      ...resolvedGroups.map(group => ({
        name: group.name,
        group: this.translate.instant('Groups'),
        iconClass: 'far fa-users',
        object: group,
      })),
    ])

    return this.pickObjects({
      items,
      searchFields: ['object.full_name', 'object.fullName', 'object.name', 'object.username', 'object.email'],
      ...options,
    })
  }

  async pickObject <T> (options: PickerOptions<T>): Promise<T> {
    const modal = this.ngbModal.open(ObjectPickerModal)
    modal.componentInstance.options = {
      ...options,
      multiple: false,
    }
    return (await modal.result)[0]
  }

  pickObjects <T> (options: PickerOptions<T>): Promise<T[]> {
    const modal = this.ngbModal.open(ObjectPickerModal)
    modal.componentInstance.options = options
    return modal.result
  }
}
